import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ClipboardTextIcon } from '@phosphor-icons/react'

import { Card, Pagination, ToastContext } from '@reveni-io/reveni-ui'

import { viewTypes } from '@/common/components/FilterViews/constants'
import FilterViews from '@/common/components/FilterViews/FilterViews'
import useViews from '@/common/components/FilterViews/hooks/useViews/useViews'
import PageSizeSelector from '@/common/components/PageSizeSelector/PageSizeSelector'
import TitlePage from '@/common/components/TitlePage/TitlePage'
import AppContext from '@/common/context/app.context'
import useFilters from '@/common/hooks/useFilters/useFilters'
import useLocations from '@/common/hooks/useLocations/useLocations'
import useRegions from '@/common/hooks/useRegions/useRegions'
import useStoreProductCategories from '@/common/hooks/useStoreProductCategories/useStoreProductCategories'
import InventoryHistoryDrawer from '@/inventory/components/InventoryHistoryDrawer/InventoryHistoryDrawer'

import CheckAllProducts from './CheckAllProducts/CheckAllProducts'
import EditItemInfoDrawer from './EditItemInfoDrawer/EditItemInfoDrawer'
import Filters from './Filters/Filters'
import FixInventoryItemsDrawer from './FixInventoryItemsDrawer/FixInventoryItemsDrawer'
import FixInventoryItemsModal from './FixInventoryItemsModal/FixInventoryItemsModal'
import useCustomsRequirements from './hooks/useCustomsRequirements/useCustomsRequirements'
import useEditItemInfo from './hooks/useEditItemInfo/useEditItemInfo'
import useFixInventoryItems from './hooks/useFixInventoryItems/useFixInventoryItems'
import useImportInventoryData from './hooks/useImportInventoryData/useImportInventoryData'
import useInventoryMetrics from './hooks/useInventoryMetrics/useInventoryMetrics'
import useNavigateItems from './hooks/useNavigateItems/useNavigateItems'
import useInventoryItems from './hooks/useProductInventoryItems/useProductInventoryItems'
import useSelectedProducts from './hooks/useSelectedProducts/useSelectedProducts'
import ImportInventoryDataDrawer from './ImportInventoryDataDrawer/ImportInventoryDataDrawer'
import InventoryItemsTable from './InventoryItemsTable/InventoryItemsTable'
import InventoryStatistics from './InventoryStatistics/InventoryStatistics'
import { defaultViews, filterNames, filters } from './constants'
import { getInventoryRequestParams } from './helpers'
import * as Styles from './styles'

const InventoryPage = () => {
  const { t } = useTranslation()
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const [showHistory, setShowHistory] = useState(false)

  const handleOpenHistory = () => setShowHistory(true)
  const handleCloseHistory = () => setShowHistory(false)

  const { productsInventoryItems, loading, next, previous, getProductsInventoryItems } = useInventoryItems({
    storeId: info?.selectedStore?.id,
  })
  const { selectedProducts, setSelectedProducts, onCheckProduct, onCheckAllProducts } = useSelectedProducts({
    products: productsInventoryItems,
  })
  const {
    inventoryMetrics,
    loading: loadingInventoryMetrics,
    fetchInventoryMetrics,
    applyInventoryMetricsFilters,
  } = useInventoryMetrics({
    storeId: info.selectedStore.id,
  })
  const { selectedView, setSelectedView } = useViews({
    storeId: info?.selectedStore?.id,
    defaultViews,
    type: viewTypes.inventory,
  })

  const handleFetchFilteredData = params => {
    const requestParams = getInventoryRequestParams(params)

    getProductsInventoryItems({ storeId: info.selectedStore.id, params: requestParams })
    applyInventoryMetricsFilters(requestParams)
  }

  const {
    updateFilter,
    query,
    pageSize,
    missing,
    country,
    weightLte,
    weightGte,
    unitCostGte,
    unitCostLte,
    category,
    productStatus,
    hasExportPrice,
    exportPriceCountry,
    refetch: refetchItems,
    onChangeView,
    getFilters,
  } = useFilters({
    filters: filters,
    doInitialRequest: true,
    fetchData: handleFetchFilteredData,
    filterValues: {},
    nameOfFiltersInContext: 'inventoryFilters',
    prevCursor: previous,
    nextCursor: next,
  })
  const { regions, loading: loadingRegions } = useRegions()
  const { locations, fetchingLocations } = useLocations({ storeId: info?.selectedStore?.id })
  const { customsRequirements, loadingCustomsRequirements } = useCustomsRequirements({
    storeId: info?.selectedStore?.id,
  })
  const { productCategories, fetchingProductCategories } = useStoreProductCategories({
    storeId: info?.selectedStore?.id,
    doInitialRequest: true,
  })
  const {
    showEditItemInfoDrawer,
    onOpenEditCustomsInformationDrawer,
    onCloseEditCustomsInformationDrawer,
    selectedItem,
    onChangeSelectedItem,
    onSaveChanges,
    savingChanges,
    selectedProduct,
    applyInfoToAllVariants,
    onChangeApplyInfoToAllVariants,
    currencies,
  } = useEditItemInfo({ storeId: info.selectedStore.id, refetchItems, setToastInfo })
  const {
    showFixInventoryItemsModal,
    openFixInventoryItemsModal,
    closeFixInventoryItemsModal,
    showFixInventoryItemsDrawer,
    onStartFixInventoryItems,
    closeFixInventoryItemsDrawer,
    draftItems,
    progressItems,
    onConfirmChanges,
    confirmingChanges,
  } = useFixInventoryItems({
    storeId: info?.selectedStore?.id,
    refetchItems,
    setToastInfo,
    fetchInventoryMetrics,
    selectedProductIds: selectedProducts,
    setSelectedProducts,
  })

  const getExportFilters = () => getInventoryRequestParams(getFilters())

  const {
    showImportInventoryDataDrawer,
    openImportInventoryDataDrawer,
    closeImportInventoryDataDrawer,
    onSelectInventoryFile,
    onDeleteInventoryFile,
    inventoryFile,
    inventoryError,
    setInventoryError,
    exportEmails,
    setExportEmails,
    exportTemplate,
    setExportTemplate,
    exportFormat,
    setExportFormat,
    onExportInventoryItems,
    onImportInventoryFile,
    uploading,
    exportedInventory,
    importInProgress,
    importCompleted,
    onCancelImport,
    importResult,
    showImportResultModal,
    onCloseImportResultModal,
    exportingInventory,
  } = useImportInventoryData({
    storeId: info?.selectedStore?.id,
    userEmail: info?.userInfo?.email,
    setToastInfo,
    getFilters: getExportFilters,
    refetchItems,
    fetchInventoryMetrics,
  })
  const { onPrevItem, onNextItem, hasPrev, hasNext } = useNavigateItems({
    items: selectedProduct?.inventory_items,
    selectedItemId: selectedItem?.id,
  })

  const handlePrevItem = () => {
    const prevItem = onPrevItem()

    if (prevItem) {
      onChangeSelectedItem(prevItem)
      onChangeApplyInfoToAllVariants(false)
    }
  }

  const handleNextItem = () => {
    const nextItem = onNextItem()

    if (nextItem) {
      onChangeSelectedItem(nextItem)
      onChangeApplyInfoToAllVariants(false)
    }
  }

  const onChangeFilter = (type, data, doRequest) => {
    if (type !== filterNames.cursor) {
      updateFilter(filterNames.cursor, '', false)
    }

    updateFilter(type, data, doRequest)
  }

  const handleChangeView = view => {
    setSelectedView(view)
    onChangeView(view)
  }

  return (
    <Styles.MainContainer>
      <Styles.TitlePageContainer>
        <TitlePage Icon={ClipboardTextIcon}>
          {t('Inventario')} - {info?.selectedStore?.name}
        </TitlePage>
      </Styles.TitlePageContainer>
      <InventoryStatistics
        loading={loadingInventoryMetrics || loadingRegions || fetchingProductCategories || fetchingLocations}
        globalScorePercentage={inventoryMetrics?.global_score_percentage}
        totalProducts={inventoryMetrics?.total_products}
        hsCodesPercentage={inventoryMetrics?.hs_codes_percentage}
        missingHsCodesCount={inventoryMetrics?.missing_hs_codes_count}
        identifiersPercentage={inventoryMetrics?.identifiers_percentage}
        missingIdentifiersCount={inventoryMetrics?.missing_identifiers_count}
        countryOfOriginPercentage={inventoryMetrics?.country_of_origin_percentage}
        missingCountryOfOriginCount={inventoryMetrics?.missing_country_of_origin_count}
        weightPercentage={inventoryMetrics?.weight_percentage}
        missingWeightCount={inventoryMetrics?.missing_weight_count}
        unitCostPercentage={inventoryMetrics?.unit_cost_percentage}
        missingUnitCostCount={inventoryMetrics?.missing_unit_cost_count}
        exportPricesPercentage={inventoryMetrics?.export_details_percentage}
        missingExportPricesCount={inventoryMetrics?.missing_export_details_count}
        onChangeFilter={onChangeFilter}
      />
      <FilterViews
        customViews={[]}
        selectedView={selectedView}
        onChangeView={handleChangeView}
        defaultViews={defaultViews}
        showOptions={true}
        onImportInventoryData={openImportInventoryDataDrawer}
        onPageHistory={handleOpenHistory}
      />
      <Styles.CardContainer>
        <Card showBorder={false}>
          <Styles.ProductsContainer>
            <Filters
              onApplyFilters={refetchItems}
              filters={{
                missing,
                query,
                country,
                weightLte,
                weightGte,
                unitCostGte,
                unitCostLte,
                category,
                productStatus,
                hasExportPrice,
                exportPriceCountry,
              }}
              onChangeFilter={onChangeFilter}
              regions={regions || []}
              productCategories={productCategories || []}
            />
            <CheckAllProducts
              selectedProducts={selectedProducts}
              products={productsInventoryItems}
              onCheckAllProducts={onCheckAllProducts}
              onAiAssistant={openFixInventoryItemsModal}
            />
            <InventoryItemsTable
              loading={loading}
              products={productsInventoryItems}
              onClickInItem={onOpenEditCustomsInformationDrawer}
              currency={info?.selectedStore?.currency}
              selectedProducts={selectedProducts}
              onCheckProduct={onCheckProduct}
            />
          </Styles.ProductsContainer>
          <Styles.PaginationContainer>
            <PageSizeSelector
              value={pageSize}
              onChange={newPageSize => onChangeFilter(filterNames.pageSize, newPageSize)}
            />
            <Pagination
              next={next}
              previous={previous}
              onChangePage={(_, cursor) => onChangeFilter(filterNames.cursor, cursor)}
              isCursorPagination
            />
          </Styles.PaginationContainer>
        </Card>
      </Styles.CardContainer>
      <EditItemInfoDrawer
        item={selectedItem}
        product={selectedProduct}
        onClose={onCloseEditCustomsInformationDrawer}
        show={showEditItemInfoDrawer}
        loading={loadingRegions || loadingCustomsRequirements}
        regions={regions || []}
        customsRequirements={customsRequirements}
        locations={locations || []}
        onChange={onChangeSelectedItem}
        onConfirm={onSaveChanges}
        savingChanges={savingChanges}
        currency={info?.selectedStore?.currency}
        applyInfoToAllVariants={applyInfoToAllVariants}
        onChangeApplyInfoToAllVariants={onChangeApplyInfoToAllVariants}
        currencies={currencies}
        onPrevItem={hasPrev && handlePrevItem}
        onNextItem={hasNext && handleNextItem}
        provider={info?.selectedStore?.provider}
      />
      <FixInventoryItemsModal
        show={showFixInventoryItemsModal}
        onClose={closeFixInventoryItemsModal}
        onStart={onStartFixInventoryItems}
      />
      <FixInventoryItemsDrawer
        show={showFixInventoryItemsDrawer}
        onClose={closeFixInventoryItemsDrawer}
        draftItems={draftItems}
        progressItems={progressItems}
        showHts={true}
        confirmingDraftItems={confirmingChanges}
        onConfirmChanges={onConfirmChanges}
      />
      <ImportInventoryDataDrawer
        onClose={closeImportInventoryDataDrawer}
        show={showImportInventoryDataDrawer}
        onSelectInventoryFile={onSelectInventoryFile}
        onDeleteInventoryFile={onDeleteInventoryFile}
        inventoryFile={inventoryFile}
        onImport={onImportInventoryFile}
        uploading={uploading}
        importInProgress={importInProgress}
        importCompleted={importCompleted}
        onCancelImport={onCancelImport}
        importResult={importResult}
        showImportResultModal={showImportResultModal}
        onCloseImportResultModal={onCloseImportResultModal}
        userEmail={info?.userInfo?.email}
        exportEmails={exportEmails}
        onChangeExportEmails={setExportEmails}
        exportTemplate={exportTemplate}
        onChangeExportTemplate={setExportTemplate}
        exportFormat={exportFormat}
        onChangeExportFormat={setExportFormat}
        onExportInventoryItems={onExportInventoryItems}
        inventoryError={inventoryError}
        onError={setInventoryError}
        exportedInventory={exportedInventory}
        exportingInventory={exportingInventory}
      />
      <InventoryHistoryDrawer
        show={showHistory}
        onClose={handleCloseHistory}
        returnPortalId={info?.selectedReturnPortal?.id}
      />
    </Styles.MainContainer>
  )
}

export default InventoryPage
